import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, X, Users, Pencil, Trash2 } from 'lucide-react';
import { useToast } from '../context/ToastContext';

const roleColor = { admin: 'badge-red', manager: 'badge-purple', employee: 'badge-blue' };

const UserModal = ({ onClose, onSave, initial }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(
    initial
      ? { name: initial.name || '', email: initial.email || '', role: initial.role || 'employee', password: '' }
      : { name: '', email: '', password: '', role: 'employee' }
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const { showToast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.password) payload.password = form.password;
      if (isEdit) await axios.put(`/api/users/${initial.id}`, payload);
      else await axios.post('/api/users', { ...payload, password: form.password });
      showToast(isEdit ? 'Team member updated' : 'Team member added', 'success');
      onSave();
    } catch (err) { showToast(err.response?.data?.message || 'Error', 'error'); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Member' : 'Add Team Member'}</div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="input-group"><label className="input-label">Full Name *</label><input className="input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ahmed Mohamed" /></div>
            <div className="input-group"><label className="input-label">Email *</label><input type="email" className="input" required value={form.email} onChange={e => set('email', e.target.value)} placeholder="ahmed@theblackdiv.com" /></div>
            <div className="input-group">
              <label className="input-label">{isEdit ? 'New Password (leave blank to keep)' : 'Password *'}</label>
              <input type="password" className="input" required={!isEdit} value={form.password} onChange={e => set('password', e.target.value)} placeholder={isEdit ? 'Leave blank to keep current' : 'Strong password'} />
            </div>
            <div className="input-group"><label className="input-label">Role</label>
              <select className="input select" value={form.role} onChange={e => set('role', e.target.value)}>
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary">{isEdit ? 'Save Changes' : 'Add Member'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const Team = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const { showToast } = useToast();

  const load = () => { axios.get('/api/users').then(r => setUsers(r.data.data.filter(u => u.role !== 'client'))).catch(console.error).finally(() => setLoading(false)); };
  useEffect(load, []);

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete "${user.name}" permanently? Their assigned tasks will become unassigned.`)) return;
    try { await axios.delete(`/api/users/${user.id}`); showToast('Team member deleted', 'success'); load(); }
    catch (err) { showToast(err.response?.data?.message || 'Failed to delete member', 'error'); }
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div className="page-content">
      {modal && (
        <UserModal
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
          initial={modal.user || undefined}
        />
      )}
      <div className="flex-between mb-24">
        <div><h1 style={{ fontSize: 20, fontWeight: 700 }}>Team</h1><p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{users.length} members</p></div>
        <button className="btn btn-primary" onClick={() => setModal({})}><Plus size={16} /> Add Member</button>
      </div>
      {!users.length ? <div className="empty-state"><Users /><p>No team members yet</p></div> : (
        <div className="grid-3">
          {users.map(u => {
            const initials = u.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
            return (
              <div key={u.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: u.isActive ? 'var(--border-light)' : 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, flexShrink: 0, opacity: u.isActive ? 1 : 0.5 }}>{initials}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {u.name}
                    {!u.isActive && <span className="badge badge-gray" style={{ fontSize: 10 }}>Inactive</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`badge ${roleColor[u.role] || 'badge-gray'}`} style={{ fontSize: 11 }}>{u.role}</span>
                    {u.lastLogin && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Last: {new Date(u.lastLogin).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                  <button onClick={() => setModal({ user: u })} title="Edit"
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => deleteUser(u)} title="Delete"
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Team;
